import { defineMessages } from 'react-intl';

export default defineMessages({
  title: {
    id: 'smart-rate.weekly-earnings.title',
    defaultMessage: 'How SmartRate is shaping your weekly earnings',
  },
  periodLabel: {
    id: 'smart-rate.weekly-earnings.period-label',
    defaultMessage: 'Last 8 weeks',
  },

  bannerTip: {
    id: 'smart-rate.weekly-earnings.banner-tip',
    defaultMessage:
      'A higher average payout rate often reflects periods of strong demand — You have <a>{count, plural, one {# upcoming opportunity} other {# upcoming opportunities}}</a> to potentially unlock more earnings.',
  },
  bannerCampaignTip: {
    id: 'smart-rate.weekly-earnings.banner-campaign-tip',
    defaultMessage: 'Running a campaign can help build demand in quieter periods. <a>Create a campaign</a>',
  },

  classTypesFilterLabel: {
    id: 'smart-rate.weekly-earnings.class-types-filter-label',
    defaultMessage: 'Class types',
  },
  classTypesFilterAllOption: {
    id: 'smart-rate.weekly-earnings.class-types-filter-all',
    defaultMessage: 'All types',
  },
  classTypesFilterSelectPrompt: {
    id: 'smart-rate.weekly-earnings.class-types-filter-select-prompt',
    defaultMessage: 'Select a class type',
  },

  metricTotalEarnings: {
    id: 'smart-rate.weekly-earnings.metric-total-earnings',
    defaultMessage: 'Total earnings*',
  },
  metricAveragePayoutRate: {
    id: 'smart-rate.weekly-earnings.metric-average-payout-rate',
    defaultMessage: 'Average payout rate*',
  },

  cellNoSpots: {
    id: 'smart-rate.weekly-earnings.cell-no-spots',
    defaultMessage: 'No spots',
  },
  cellNoCpBookings: {
    id: 'smart-rate.weekly-earnings.cell-no-cp-bookings',
    defaultMessage: 'No CP bookings',
  },
  cellEmpty: {
    id: 'smart-rate.weekly-earnings.cell-empty',
    defaultMessage: '-',
  },
  loadingMessage: {
    id: 'smart-rate.weekly-earnings.loading-message',
    defaultMessage: 'Loading weekly earnings data...',
  },
  errorMessage: {
    id: 'smart-rate.weekly-earnings.error-message',
    defaultMessage: 'We could not load your weekly earnings data. Please try again later.',
  },
  emptyMessage: {
    id: 'smart-rate.weekly-earnings.empty-message',
    defaultMessage: 'Weekly earnings data is not available yet.',
  },
  selectClassTypeMessage: {
    id: 'smart-rate.weekly-earnings.select-class-type-message',
    defaultMessage: 'Select a class type to see your data',
  },
  insufficientReservationsMessage: {
    id: 'smart-rate.weekly-earnings.insufficient-reservations-message',
    defaultMessage: 'Data for the selected classes is not available.',
  },
  notEnoughHistoryMessage: {
    id: 'smart-rate.weekly-earnings.not-enough-history-message',
    defaultMessage: 'SmartRate data coming soon. Stay tuned!',
  },

  legendTitle: {
    id: 'smart-rate.weekly-earnings.legend-title',
    defaultMessage: 'Understanding your earnings',
  },
  legendIntro: {
    id: 'smart-rate.weekly-earnings.legend-intro',
    defaultMessage:
      'Your total earnings are driven by two factors: your average payout rate per reservation and the number of reservations you receive. SmartRate is designed to balance these factors and maximize your total earnings.',
  },
  legendBulletLower: {
    id: 'smart-rate.weekly-earnings.legend-bullet-lower',
    defaultMessage:
      'A lower average payout rate can still lead to higher earnings if it helps drive more reservations for your class.',
  },
  legendBulletHigher: {
    id: 'smart-rate.weekly-earnings.legend-bullet-higher',
    defaultMessage:
      'A higher average payout rate can reflect strong demand, when more users are willing to book at higher credit amounts. There may be <a>opportunities</a> in these periods to unlock more earnings.',
  },
  legendBulletLow: {
    id: 'smart-rate.weekly-earnings.legend-bullet-low',
    defaultMessage:
      'If your total earnings are low, it may be due to fewer reservations, not just your average payout rate. Creating a <a>campaign</a> may help increase conversions.',
  },

  tooltipStatTotalEarnings: {
    id: 'smart-rate.weekly-earnings.tooltip-stat-total-earnings',
    defaultMessage: 'Total earnings',
  },
  tooltipStatAveragePayoutRate: {
    id: 'smart-rate.weekly-earnings.tooltip-stat-average-payout-rate',
    defaultMessage: 'Average payout rate',
  },
  tooltipStatReservations: {
    id: 'smart-rate.weekly-earnings.tooltip-stat-reservations',
    defaultMessage: 'Total ClassPass reservations',
  },
  tooltipStatUnfilledSpots: {
    id: 'smart-rate.weekly-earnings.tooltip-stat-unfilled-spots',
    defaultMessage: '% of <a>unfilled spots</a> filled by ClassPass',
  },
  tooltipCalloutExcess0to10Res0to3: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-0-10-res-0-3',
    defaultMessage:
      'Demand during this period is soft. Consider running a campaign to help generate more bookings at this time. <a>Create a campaign</a>',
  },
  tooltipCalloutExcess0to10Res4to15: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-0-10-res-4-15',
    defaultMessage:
      'Some interest during this period, but excess spots went unfilled. A campaign can help fill more spots in this period. <a>Create a campaign</a>',
  },
  tooltipCalloutExcess0to10Res16Plus: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-0-10-res-16-plus',
    defaultMessage:
      'Reservations were strong, but excess spots stayed mostly empty. A campaign can help fill more spots in this period. <a>Create a campaign</a>',
  },
  tooltipCalloutExcess11to30Res0to3: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-11-30-res-0-3',
    defaultMessage:
      'Some interest during this period, but bookings are low. A campaign can help convert that interest into reservations. <a>Create a campaign</a>',
  },
  tooltipCalloutExcess11to30Res4to15: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-11-30-res-4-15',
    defaultMessage:
      'Steady demand filled this period. Consider creating a campaign to help capture more of that demand. <a>Create a campaign</a>',
  },
  tooltipCalloutExcess11to30Res16Plus: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-11-30-res-16-plus',
    defaultMessage:
      'Strong demand filled this period — ensure you are capturing all opportunities during this period. <a>View opportunities</a>',
  },
  tooltipCalloutExcess31PlusRes0to3: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-31-plus-res-0-3',
    defaultMessage:
      'Strong interest during this period, but reservations stayed low. A campaign can help capture more of that demand. <a>Create a campaign</a>',
  },
  tooltipCalloutExcess31PlusRes4to15: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-31-plus-res-4-15',
    defaultMessage:
      'Healthy demand filled this period — ensure you are capturing all opportunities during this period. <a>View opportunities</a>',
  },
  tooltipCalloutExcess31PlusRes16Plus: {
    id: 'smart-rate.weekly-earnings.tooltip-callout-excess-31-plus-res-16-plus',
    defaultMessage:
      'This period is in high demand and excess spots are filling fast — ensure you are capturing all opportunities during this period. <a>View opportunities</a>',
  },
  tooltipNoSpotsBody: {
    id: 'smart-rate.weekly-earnings.tooltip-no-spots-body',
    defaultMessage: 'No availability open to ClassPass members',
  },
  tooltipNoSpotsCallout: {
    id: 'smart-rate.weekly-earnings.tooltip-no-spots-callout',
    defaultMessage:
      'SmartSpot may be protecting your direct demand — however there may be opportunities to open more availability to ClassPass members. <a>View opportunities</a>',
  },
  tooltipNoCpBookingsBody: {
    id: 'smart-rate.weekly-earnings.tooltip-no-cp-bookings-body',
    defaultMessage: 'Not enough ClassPass reservations to show how this period is performing',
  },
  tooltipNoCpBookingsCallout: {
    id: 'smart-rate.weekly-earnings.tooltip-no-cp-bookings-callout',
    defaultMessage: 'Running a campaign can help build demand in quieter periods. <a>Create a campaign</a>',
  },

  sheetTitle: {
    id: 'smart-rate.weekly-earnings.sheet-title',
    defaultMessage: 'SmartRate Performance',
  },
  sheetSubtitleClassTypes: {
    id: 'smart-rate.weekly-earnings.sheet-subtitle-class-types',
    defaultMessage: 'Class types: {classLabel}',
  },

  footnote: {
    id: 'smart-rate.weekly-earnings.footnote',
    defaultMessage:
      '*Excludes reservations from trial users, campaigns, corporate discounts, as well as late-cancelled and missed reservations. For complete reporting, <a>see all reservations.</a>',
  },
});
